// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '@shared/commons/DataCache';
import { OrderTypeBaseParameters } from '@shared/commons/cache/OrderParams/order-type/OrderTypeBaseParameters';
import { LinkedPriceType } from '@shared/commons/cache/SavedOrders/SavedOrderEnums';
import { Resources } from '@shared/localizations/Resources';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { Intervals } from '@shared/utils/Instruments/Intervals';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { TIF } from '@shared/utils/Trading/OrderTif';
import { OrderTif, OrderTifMap, sortTifs } from '@shared/utils/Trading/OrderTifEnum';
import { sortOrderTypes } from '@shared/utils/Trading/OrderType';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { BaseItem } from './BaseItem';
import { EventEmitter } from 'events';
import { SlTpNumericInputParameters, SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class SavedOrderItemBase extends BaseItem {
    get isExistProductTypes() {
        return this.getProductTypeComboboxItems().length > 1;
    }
    constructor(savedOrder, sessionSettings) {
        super(sessionSettings);
        this._eventEmitter = new EventEmitter();
        this.onUpdateSavedOrder = () => {
            super.fireUpdate();
            this._eventEmitter.emit('update', this);
        };
        this.onUpdateSavedOrderInstrument = () => {
            this.updateEditingInfo();
        };
        this.onUpdateSavedOrderDefaultPrices = () => {
            this.updateEditingInfo();
        };
        this.getOpeationComboboxItems = () => {
            const operationComboboxItems = [];
            operationComboboxItems.push({
                type: NewContextMenuItemType.Default,
                text: 'general.trading.Buy',
                value: OperationType.Buy
            });
            operationComboboxItems.push({
                type: NewContextMenuItemType.Default,
                text: 'general.trading.Sell',
                value: OperationType.Sell
            });
            return operationComboboxItems;
        };
        this.getOrderTypeComboboxItems = () => {
            const orderTypesComboboxItems = [];
            const orderTypes = DataCache.OrderParameterContainer.OrderTypes;
            const supportedParamObj = new OrderTypeBaseParameters(this.savedOrder.Instrument, this.savedOrder.Account);
            let supportedOrderTypes = [];
            for (const key in orderTypes) {
                const orderType = orderTypes[key];
                const isSupportedOrderType = orderType.IsSupported(supportedParamObj);
                if (!isSupportedOrderType || !this.isAllowOrderType(orderType.id())) {
                    continue;
                }
                supportedOrderTypes.push(orderType.id());
            }
            supportedOrderTypes = sortOrderTypes(supportedOrderTypes);
            for (const supportedOrderType of supportedOrderTypes) {
                orderTypesComboboxItems.push({
                    type: NewContextMenuItemType.Default,
                    text: `property.${OrderUtils.getOrderTypeLocalizationKey(supportedOrderType)}`,
                    value: supportedOrderType
                });
            }
            return orderTypesComboboxItems;
        };
        this.getTifComboboxItems = () => {
            const tifComboboxItems = [];
            const expirationTime = this.savedOrder.TIF === OrderTif.GTD ? this.savedOrder.TIFExpiration : null;
            const selectedTif = new TIF(this.savedOrder.TIF, expirationTime);
            if (isNullOrUndefined(this.savedOrder.Instrument) || isNullOrUndefined(this.savedOrder.OrderType)) {
                return { items: tifComboboxItems, selectedValue: selectedTif };
            }
            if (DataCache.OrderParameterContainer === null) {
                return { items: tifComboboxItems, selectedValue: selectedTif };
            }
            const supportedParamObj = new OrderTypeBaseParameters(this.savedOrder.Instrument, this.savedOrder.Account);
            const currentOrderType = DataCache.OrderParameterContainer.GetOrderType(this.savedOrder.OrderType);
            if (isNullOrUndefined(currentOrderType)) {
                return { items: tifComboboxItems, selectedValue: selectedTif };
            }
            const isSupportedOrderType = currentOrderType.IsSupported(supportedParamObj);
            if (!isSupportedOrderType) {
                return { items: tifComboboxItems, selectedValue: selectedTif };
            }
            const route = DataCache.Routes.getRouteByName(this.savedOrder.Instrument.Route);
            const routeTifsMap = route.DictOrderTypeTifs[this.savedOrder.OrderType];
            if (isNullOrUndefined(routeTifsMap)) {
                return { items: tifComboboxItems, selectedValue: selectedTif };
            }
            const tifs = sortTifs(Object.values(routeTifsMap));
            for (const tif of tifs) {
                tifComboboxItems.push({
                    type: NewContextMenuItemType.Default,
                    text: Resources.getResource(OrderTifMap[tif]),
                    value: tif
                });
            }
            return { items: tifComboboxItems, selectedValue: selectedTif };
        };
        this.getProductTypeComboboxItems = () => {
            const account = this.savedOrder.Account;
            const instrument = this.savedOrder.Instrument;
            const productTypesComboboxItems = [];
            if (isNullOrUndefined(account) || isNullOrUndefined(instrument)) {
                return productTypesComboboxItems;
            }
            const dict = !isNullOrUndefined(account.RiskPlan)
                ? account.RiskPlan.GetRisksForInstrument(instrument.GetInteriorID()).availableProductTypes
                : InstrumentUtils.getAllowedProductTypeDict(instrument);
            if (dict.length === 0) {
                return productTypesComboboxItems;
            }
            if (dict.length === 1 && Number(dict[0]) === ProductType.General) {
                return productTypesComboboxItems;
            }
            for (const productType of dict) {
                productTypesComboboxItems.push({
                    type: NewContextMenuItemType.Default,
                    text: InstrumentUtils.GetLocalizedProductType(instrument, productType),
                    value: productType
                });
            }
            return productTypesComboboxItems;
        };
        this.getLinkedPriceTypeComboboxItems = () => {
            const linkedPriceTypeComboboxItems = [];
            linkedPriceTypeComboboxItems.push({
                type: NewContextMenuItemType.Default,
                text: SavedOrderItemBase.LocalizeLinkPriceType(LinkedPriceType.None),
                value: LinkedPriceType.None
            });
            linkedPriceTypeComboboxItems.push({
                type: NewContextMenuItemType.Default,
                text: SavedOrderItemBase.LocalizeLinkPriceType(LinkedPriceType.Bid),
                value: LinkedPriceType.Bid
            });
            linkedPriceTypeComboboxItems.push({
                type: NewContextMenuItemType.Default,
                text: SavedOrderItemBase.LocalizeLinkPriceType(LinkedPriceType.Ask),
                value: LinkedPriceType.Ask
            });
            linkedPriceTypeComboboxItems.push({
                type: NewContextMenuItemType.Default,
                text: SavedOrderItemBase.LocalizeLinkPriceType(LinkedPriceType.Last),
                value: LinkedPriceType.Last
            });
            return linkedPriceTypeComboboxItems;
        };
        this.getLeverageComboboxItems = () => {
            const account = this.savedOrder.Account;
            const instrument = this.savedOrder.Instrument;
            const productType = this.savedOrder.ProductType;
            const leverageComboboxItems = [];
            if (isNullOrUndefined(account) || isNullOrUndefined(instrument)) {
                return leverageComboboxItems;
            }
            const isLeverageVisible = instrument.isLeverageVisible(account, productType);
            if (isLeverageVisible) {
                const leverages = instrument.getLeverages(account, productType);
                for (const leverage of leverages) {
                    leverageComboboxItems.push({
                        type: NewContextMenuItemType.Default,
                        text: leverage.toString(),
                        value: leverage
                    });
                }
            }
            return leverageComboboxItems;
        };
        this.isOvernightTradingHours = () => {
            var _a;
            const isAllowOTHTrading = (_a = this.savedOrder) === null || _a === void 0 ? void 0 : _a.IsAllowOTHTrading;
            return !isNullOrUndefined(isAllowOTHTrading) && isAllowOTHTrading;
        };
        this.savedOrder = savedOrder;
        if (!isNullOrUndefined(this.savedOrder)) {
            this.initEditingInfo();
            this.updateEditingInfo();
            this.subscribe();
        }
    }
    subscribe() {
        if (isNullOrUndefined(this.savedOrder)) {
            return;
        }
        this.savedOrder.SubscribeOnUpdate(this.onUpdateSavedOrder);
        this.savedOrder.SubscribeOnUpdateInstrument(this.onUpdateSavedOrderInstrument);
        this.savedOrder.SubscribeOnUpdateDefaultPrices(this.onUpdateSavedOrderDefaultPrices);
    }
    unsubscribe() {
        if (isNullOrUndefined(this.savedOrder)) {
            return;
        }
        this.savedOrder.UnsubscribeOnUpdate(this.onUpdateSavedOrder);
        this.savedOrder.UnsubscribeOnUpdateInstrument(this.onUpdateSavedOrderInstrument);
        this.savedOrder.UnSubscribeOnUpdateDefaultPrices(this.onUpdateSavedOrderDefaultPrices);
    }
    updateNumericParameters(columnIndex, parameters) {
        const numericParameters = this.QuickTableEditingInfoMap[columnIndex];
        numericParameters.Min = parameters.min;
        numericParameters.Max = parameters.max;
        numericParameters.Inc = parameters.step;
        numericParameters.DecimalPlaces = parameters.precision;
    }
    isAllowOrderType(orderType) {
        return true;
    }
    updatePriceNumeric(index, minValue = undefined) {
        const instrument = this.savedOrder.Instrument;
        let min = isValidNumber(minValue) ? minValue : 0;
        let decimalPlaces = 0;
        let increment = 1;
        let intervals = null;
        if (!isNullOrUndefined(instrument)) {
            const numericLimits = NumericUtils.getPriceNumericParameters(instrument);
            intervals = Intervals.GetIntervalsFromInstrument(instrument, NumericUtils.MAXVALUE);
            if (!isValidNumber(minValue)) {
                min = numericLimits.step;
            }
            decimalPlaces = numericLimits.precision;
            increment = numericLimits.step;
        }
        const priceNumeric = this.QuickTableEditingInfoMap[index];
        priceNumeric.Min = min;
        priceNumeric.Max = NumericUtils.MAXVALUE;
        priceNumeric.Inc = increment;
        priceNumeric.DecimalPlaces = decimalPlaces;
        priceNumeric.IntervalsListForNumeric = intervals;
    }
    updateSLOffsetNumeric(slColumnIndex) {
        var _a;
        const instrument = this.savedOrder.Instrument;
        const offsetType = (_a = SlTpUtils.getSLVisualOffsetType(this.savedOrder.Instrument, this.savedOrder.SLTPHolder)) !== null && _a !== void 0 ? _a : SlTpUtils.getDefaultVisualOffsetType(this.savedOrder.Instrument);
        const sltpNumericInputParameters = new SlTpNumericInputParameters(instrument, offsetType, this.savedOrder.BasePrice, this.savedOrder.Operation === OperationType.Buy, true);
        const slNumericParameters = SlTpUtils.getSlTpNumericParameters(sltpNumericInputParameters);
        slNumericParameters.min = 0;
        this.updateNumericParameters(slColumnIndex, slNumericParameters);
    }
    updateSLLOffsetNumeric(sllColumnIndex) {
        this.updateSLOffsetNumeric(sllColumnIndex);
        const sllNumericParameters = this.QuickTableEditingInfoMap[sllColumnIndex];
        sllNumericParameters.Min = -NumericUtils.MAXVALUE;
    }
    updateTPOffsetNumeric(tpColumnIndex) {
        var _a;
        const instrument = this.savedOrder.Instrument;
        const offsetType = (_a = SlTpUtils.getTPVisualOffsetType(this.savedOrder.Instrument, this.savedOrder.SLTPHolder)) !== null && _a !== void 0 ? _a : SlTpUtils.getDefaultVisualOffsetType(this.savedOrder.Instrument);
        const sltpNumericInputParameters = new SlTpNumericInputParameters(instrument, offsetType, this.savedOrder.BasePrice, this.savedOrder.Operation === OperationType.Buy, false);
        const tpNumericParameters = SlTpUtils.getSlTpNumericParameters(sltpNumericInputParameters);
        tpNumericParameters.min = 0;
        this.updateNumericParameters(tpColumnIndex, tpNumericParameters);
    }
    updateTicksOffsetNumeric(index, isAllowNegative) {
        const instrument = this.savedOrder.Instrument;
        const offsetType = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(this.savedOrder.Instrument);
        const sltpNumericInputParameters = new SlTpNumericInputParameters(instrument, offsetType);
        const offsetNumericParameters = SlTpUtils.getSlTpNumericParameters(sltpNumericInputParameters);
        if (isAllowNegative) {
            offsetNumericParameters.min = -offsetNumericParameters.max;
        }
        this.updateNumericParameters(index, offsetNumericParameters);
    }
    updateAmountNumeric(index) {
        const account = this.savedOrder.Account;
        const instrument = this.savedOrder.Instrument;
        const productType = this.savedOrder.ProductType;
        const quantityNumericSettings = Quantity.getQuantityNumericSettings(instrument, account, WDSettingsUtils.displayAmountInLots(), productType);
        const amountNumeric = this.QuickTableEditingInfoMap[index];
        amountNumeric.Min = quantityNumericSettings.minValue;
        amountNumeric.Max = quantityNumericSettings.maxValue;
        amountNumeric.Inc = quantityNumericSettings.step;
        amountNumeric.DecimalPlaces = quantityNumericSettings.decimalPrecision;
    }
    updateCashNumeric(index) {
        const cashNumericSettings = Quantity.getCashNumericSettings(this.savedOrder.Instrument, this.savedOrder.Account);
        const cashNumeric = this.QuickTableEditingInfoMap[index];
        cashNumeric.Min = cashNumericSettings.minValue;
        cashNumeric.Max = cashNumericSettings.maxValue;
        cashNumeric.Inc = cashNumericSettings.step;
        cashNumeric.DecimalPlaces = cashNumericSettings.decimalPrecision;
    }
    GetCurrentAccount() {
        var _a;
        return (_a = this.savedOrder) === null || _a === void 0 ? void 0 : _a.Account;
    }
    GetCurrentInstrument() {
        var _a;
        return (_a = this.savedOrder) === null || _a === void 0 ? void 0 : _a.Instrument;
    }
    subsribeOnUpdate(callback) {
        this._eventEmitter.on('update', callback);
    }
    unsubsribeOnUpdate(callback) {
        this._eventEmitter.off('update', callback);
    }
    Dispose() {
        this.unsubscribe();
        super.Dispose();
    }
    static LocalizeLinkPriceType(type) {
        switch (type) {
            case LinkedPriceType.None:
                return Resources.getResource('property.None');
            case LinkedPriceType.Bid:
                return Resources.getResource('property.Bid');
            case LinkedPriceType.Ask:
                return Resources.getResource('property.Ask');
            case LinkedPriceType.Last:
                return Resources.getResource('property.Last');
            default:
                return '';
        }
    }
}
